import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface FeaturedCategory {
  category: string;
  title: string;
  description: string;
  imageUrl: string;
}

export interface ServiceCategoryPricingRow {
  category: string | null;
  price: string | number | null;
}

/**
 * Homepage featured categories (page_content) and service pricing rows for
 * the home/Services component. Returns both datasets so the component can
 * derive min-prices with the latest currency setting on every render.
 */
export function useHomepageFeaturedCategories(orgId: string | undefined) {
  return useQuery({
    queryKey: ['homepage-featured-categories', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<FeaturedCategory[]> => {
      const { data, error } = await supabase
        .from('page_content')
        .select('content')
        .eq('organization_id', orgId!)
        .eq('page', 'homepage')
        .eq('section', 'featured_categories')
        .maybeSingle();

      if (error) throw error;

      return (data?.content as unknown as FeaturedCategory[] | undefined) ?? [];
    },
  });
}

export function useHomepageServicePricing(orgId: string | undefined) {
  return useQuery({
    queryKey: ['homepage-service-pricing', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<ServiceCategoryPricingRow[]> => {
      const { data, error } = await supabase
        .from('services')
        .select('category, price')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .not('category', 'is', null);

      if (error) throw error;

      return (data ?? []) as ServiceCategoryPricingRow[];
    },
  });
}
